import type { Core, Schema } from "../../..";
import type { UITemplateFields } from "../../../types/schema-fields/ui-template-fields";
import type {
	GlobalState,
	SchemaType,
	SchemaTypeWithComponentProp,
} from "../misc/types";
import type { Parser } from "../parser";

import {
	contentTypeSuffix as cts,
	interfaceSuffix as isf,
	publicApiSuffix as paf,
} from "../misc/constants";
import { getTypeFromField } from "../misc/utils";

export class UIAndDataPropsParser implements Parser {
	parse(schema: Record<string, SchemaType>, config: Core.Config) {
		const schemasArray = convertSchemasObjectsToArray(schema);

		const interfaceSuffix = config.autotypes?.interfaceSuffix || isf;

		const { ARRAY_OF_MODULE_SCHEMAS } = getModuleSchemas(schemasArray);

		return schemasArray
			.map((schema) => {
				let GS: GlobalState = {
					kind: "component",
					contentTypeSuffixStr: "",
					contentTypeName: "",
					contentTypeKind: "",
					relatedContentTypeNames: "",
					componentName: "",
					hasDistributorData: false,
					getStaticData: false,
					interfaceForPublicAPI: "",
					// to deprecate...
					relationsGeneric:
						schema._autoType?.relations || false
							? "<Relations = unknown, FromList = unknown, FromDistributor = true>"
							: "",
				};

				// -----------------------------------------------------------------
				// CONTENT TYPE SCHEMA
				// -----------------------------------------------------------------
				if (isContentTypeSchema(schema)) {
					const localState = processContentTypeSchema(schema, config);
					GS = { ...GS, ...localState };
				}

				// -----------------------------------------------------------------
				//  UI SCHEMA
				// -----------------------------------------------------------------
				if (isUISchema(schema)) {
					const localState = processUISchema(schema);
					GS = { ...GS, ...localState };
				}

				// -----------------------------------------------------------------
				//  EVERY FIELDS IN SCHEMAS
				// -----------------------------------------------------------------
				const griddoFieldProps = getPropsFromFields(
					schema,
					ARRAY_OF_MODULE_SCHEMAS,
					schema,
					GS,
					config.autotypes,
				);

				return `
				export interface ${schema.component}${GS.contentTypeSuffixStr}${interfaceSuffix}${GS.relationsGeneric} ${getExtra(schema)} {
					${GS.contentTypeName}
					${GS.contentTypeKind}
					${GS.relatedContentTypeNames}
					${GS.componentName}
					${griddoFieldProps}
					${isCategoryInterface(schema) ? "title?: string; code?: string" : ""}
				}
				${GS.interfaceForPublicAPI}
			`;
			})
			.join("");
	}
}

function extendsType(types: Array<string>) {
	return types.filter((typeStr) => typeStr.length).length < 1
		? ""
		: `extends ${types.join(", ")}`;
}

function getExtra(schema: SchemaTypeWithComponentProp) {
	const extraListTemplateProps = isListTemplate(schema)
		? "Fields.ExtraListTemplateProps"
		: "";
	return extendsType([extraListTemplateProps]);
}

function isUISchema(
	schema: SchemaTypeWithComponentProp,
): schema is
	| Schema.Component
	| Schema.ContentTypeModule
	| Schema.MultiPageModule
	| Schema.Module
	| Schema.Template
	| Schema.ListTemplate {
	return "schemaType" in schema;
}

function isContentTypeSchema(
	schema: SchemaTypeWithComponentProp,
): schema is
	| (Schema.SimpleContentType & { component: string })
	| (Schema.PageContentType & { component: string }) {
	return "fromPage" in schema;
}

function isCategoryInterface(schema: SchemaTypeWithComponentProp) {
	return "fromPage" in schema && !schema.fromPage && schema.taxonomy === true;
}

function isListTemplate(schema: SchemaTypeWithComponentProp) {
	return (
		"schemaType" in schema &&
		schema.schemaType === "template" &&
		(schema.type.mode === "list" || schema.type.mode === "paginated-data")
	);
}

/**
 * Convert an object of schemas into an array of objects of these schemas.
 *
 * @example
 * input:
 * `schemas` is an object of schemas like so:
 *	{
 *		BasicContent: {
 *			component: "BasicContent",
 *			...
 *		},
 *		...
 *	}
 *
 * output:
 * [ {component: "BasicContent"}, {component: "NewsTemplate"}, ... ]
 */
function convertSchemasObjectsToArray(
	objectSchemas: Record<string, SchemaType>,
): Array<SchemaTypeWithComponentProp> {
	return (Object.keys(objectSchemas) as Array<string>).map((componentName) => {
		return {
			// This component prop is for the ContentType schemas,
			// ui schemas have already one.
			...objectSchemas[componentName],
			component: componentName,
		};
	});
}

function processContentTypeSchema(
	schema:
		| (Schema.SimpleContentType & { component: string })
		| (Schema.PageContentType & { component: string }),
	config: Core.Config,
): GlobalState {
	// ContentType names list
	const contentTypePageListNames: Array<string> = [];
	const contentTypeSimpleListNames: Array<string> = [];
	const contentTypeTaxonomyListNames: Array<string> = [];

	const kind = schema.fromPage ? "pageContentType" : "simpleContentType";

	const contentTypeSuffix = config.autotypes?.contentTypeSuffix || cts;
	const publicApiSuffix = config.autotypes?.publicApiSuffix || paf;
	const interfaceSuffix = config.autotypes?.interfaceSuffix || isf;

	// Add page ContentType name to the array
	if (kind === "pageContentType") {
		contentTypePageListNames.push(`"${schema.component}"`);
	}
	// Add simple ContentType name to the array
	if (kind === "simpleContentType" && !schema.taxonomy) {
		contentTypeSimpleListNames.push(`"${schema.component}"`);
	}
	// Add taxonomy (category) simple ContentType name to the array
	if (kind === "simpleContentType" && schema.taxonomy) {
		contentTypeTaxonomyListNames.push(`"${schema.component}"`);
	}

	let relatedContentTypeNames: any = "";
	if ("schema" in schema) {
		const relatedContentTypeNamesValues = schema.schema.fields
			.map((field) => {
				if ("source" in field) {
					return field?.source;
				}

				return false;
			})
			.filter(Boolean);

		relatedContentTypeNames = relatedContentTypeNamesValues.length
			? `__relatedContentTypeNames: ${JSON.stringify(relatedContentTypeNamesValues)}`
			: "";
	}

	// En el caso de los schemas de ContentType se pondrá siempre el
	// genérico a unknown para así poder pasarle el relations en el
	// componente de React, por ejemplo cuando se utilice el Type en
	// un hook como `useContentType`
	// `useList<AutoTypes.MEMBERContentType<true>>()`
	//                                       ^
	//                                   relations
	const relationsGeneric =
		"<Relations = unknown, FromList = unknown, FromDistributor = true>";

	const interfaceForPublicAPI = `
				/** Type to be consumed by public API: useList(), etc.. */
				export type ${schema.component}${contentTypeSuffix}${publicApiSuffix}${interfaceSuffix}<R extends Core.ContentTypeRelations = false> = ${schema.component}${contentTypeSuffix}${interfaceSuffix}<R, true>;
				`;

	return {
		kind,
		componentName: "",
		contentTypeName: `__contentTypeName: "${schema.component}";`,
		contentTypeKind: `__contentTypeKind: "${kind}";`,
		contentTypeSuffixStr: contentTypeSuffix,
		getStaticData: false,
		hasDistributorData: false,
		relatedContentTypeNames,
		relationsGeneric,
		interfaceForPublicAPI,
	};
}

function processUISchema(
	schema:
		| Schema.Component
		| Schema.ContentTypeModule
		| Schema.MultiPageModule
		| Schema.Module
		| Schema.Template
		| Schema.ListTemplate
		| Schema.FormTemplate,
): GlobalState {
	return {
		kind: schema.schemaType,
		componentName: `component: "${schema.component}"`,
		contentTypeName: "",
		contentTypeKind: "",
		contentTypeSuffixStr: "",
		relatedContentTypeNames: "",
		relationsGeneric: "",
		interfaceForPublicAPI: "",
		hasDistributorData: !!schema.default.hasDistributorData,
		getStaticData: !!schema.default.getStaticData,
	};
}

function getPropsFromFields(
	schema: SchemaTypeWithComponentProp,
	arrayOfSchemaModules: Array<Schema.Module | undefined>,
	allSchemas: any,
	GS: GlobalState,
	autotypesConfig: Core.Config["autotypes"],
) {
	return getAllFields(schema)
		.map((field) => {
			return getTypeFromField(field, GS.kind, {
				hasDistributorData: GS.hasDistributorData,
				getStaticData: GS.getStaticData,
				moduleSchemas: arrayOfSchemaModules,
				schema,
				allSchemas,
				autotypesConfig,
			});
		})
		.filter(Boolean)
		.join("");
}

function getModuleSchemas(schemas: Array<SchemaTypeWithComponentProp>) {
	const ARRAY_OF_MODULE_SCHEMAS = schemas
		.map((schema) => {
			if ("schemaType" in schema && schema.schemaType === "module") {
				return schema;
			}
			return undefined;
		})
		.filter(Boolean);

	return {
		ARRAY_OF_MODULE_SCHEMAS,
	};
}

/** Get all fields from one schema */
function getAllFields(schema: SchemaType) {
	// ContentType
	// Simple, Page
	if ("fromPage" in schema) {
		// AutoTypes doesn't generate types from categories
		if (!schema.taxonomy) {
			return schema.schema.fields;
		}
		// hack???
		return [];
	}

	// Templates
	if (schema.schemaType === "template") {
		const content = (schema.content as Array<UITemplateFields>) || [];
		const config = (schema.config as Array<UITemplateFields>) || [];
		return [...content, ...config];
	}

	if (schema.schemaType === "formTemplate") {
		const content = (schema.content as Array<UITemplateFields>) || [];
		const config = (schema.config as Array<UITemplateFields>) || [];
		return [...content, ...config];
	}

	// Objects, components and modules
	if (
		schema.schemaType === "component" ||
		schema.schemaType === "module" ||
		schema.schemaType === "formComponent"
	) {
		return schema.configTabs.flatMap((tab) => tab.fields);
	}

	throw new Error(`Wrong schema ${JSON.stringify(schema, null, 2)}`);
}

export {
	convertSchemasObjectsToArray,
	getExtra,
	getModuleSchemas,
	getPropsFromFields,
	isCategoryInterface,
	isContentTypeSchema,
	isUISchema,
	processContentTypeSchema,
	processUISchema,
};
